import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { toastUtils } from '@/common/utils/toastUtils';
import { useAuthStore } from '@/store/useAuthStore';
import { useLocation } from 'react-router-dom';
import { spaceService } from '@/services/spaceService';
import { authService } from '@/services/authService';
import { companyUserService, CompanyUserDto, ApplyCompanyUserDto } from '@/services/companyUserService';
import { useNavigate } from 'react-router-dom';
import { useNavigationStore } from '@/common/store';
import config from '@/config';
import Avatar from '@/common/components/avatar/Avatar';
import {
  Building2,
  Rocket,
  TrendingUp,
  Lightbulb,
  ExternalLink,
  Plus,
  Check,
  Sparkles,
  Crown,
  ChevronRight,
  Zap,
  Users,
  Award,
  Settings,
  ShieldCheck,
  ShieldX,
  UserCheck,
  Inbox,
  Mail,
  Search,
  X,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';


interface SpaceInfo {
  type: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  isActive: boolean;
  canCreate: boolean;
}

type TabType = 'space' | 'master' | 'application';

const CompanySpaceManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {  setHeaderTitle, clearBackNavigation, setBackNavigation} = useNavigationStore();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('space');
  const [companyUsers, setCompanyUsers] = useState<CompanyUserDto[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [masterActionLoading, setMasterActionLoading] = useState<number | null>(null);
  const [showAddMasterPanel, setShowAddMasterPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // 가입 신청 관련 상태
  const [applications, setApplications] = useState<ApplyCompanyUserDto[]>([]);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);
  
  const {user} = useAuthStore();
  const companyUser = user?.companyUsers.find((cu) => cu.company.code === location.pathname.split("/").pop());
  const company = companyUser?.company;
  
  // 가입 신청 필터링 (대기중인 신청만)
  const pendingApplications = applications.filter(app => app.isAccepted === undefined || app.isAccepted === null);
  const processedApplications = applications.filter(app => app.isAccepted !== undefined && app.isAccepted !== null);
  
  // 마스터와 일반 멤버 분리
  const masterUsers = companyUsers.filter(u => u.isMaster);
  const nonMasterUsers = companyUsers.filter(u => !u.isMaster);
  
  // 검색 필터링
  const filteredNonMasterUsers = nonMasterUsers.filter(u => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      (u.name && u.name.toLowerCase().includes(query)) ||
      (u.email && u.email.toLowerCase().includes(query)) ||
      (u.position && u.position.toLowerCase().includes(query))
    );
  });

  if(!company || !companyUser) {
    return (
      <PageContainer>
        <EmptyState>
          <EmptyIconWrapper>
            <Building2 size={48} />
          </EmptyIconWrapper>
          <EmptyTitle>회사 정보를 찾을 수 없습니다</EmptyTitle>
          <EmptyDescription>올바른 경로로 접근해주세요.</EmptyDescription>
        </EmptyState>
      </PageContainer>
    );
  }

  /**
   * 모든 가능한 공간 타입 정의
   */
  const allSpaces: SpaceInfo[] = [
    {
      type: 'STARTUP',
      name: '스타트업',
      icon: <Rocket size={24} />,
      description: '스타트업을 위한 대시보드와 서비스',
      isActive: company.service.includes('STARTUP'),
      canCreate: !company.service.includes('STARTUP'),
    },
    {
      type: 'INVESTOR',
      name: '투자자',
      icon: <TrendingUp size={24} />,
      description: '투자 관리 및 딜 소싱',
      isActive: company.service.includes('INVESTOR'),
      canCreate: !company.service.includes('INVESTOR'),
    },
    {
      type: 'INNOVATE',
      name: '선발 프로그램',
      icon: <Lightbulb size={24} />,
      description: '프로그램 운영 및 관리',
      isActive: company.service.includes('INNOVATE'),
      canCreate: !company.service.includes('INNOVATE'),
    },
  ];

  const activeSpaces = allSpaces.filter(space => space.isActive);
  const availableSpaces = allSpaces.filter(space => space.canCreate);

  /**
   * 공간 생성 핸들러
   */
  const handleCreateSpace = async (spaceType: string) => {
    setIsLoading(true);
    try {
      if(!companyUser.emailVerified) {
        toastUtils.error('이메일 인증을 먼저 해주세요.');
        return;
      }
      const result = await spaceService.applySpace(company.id, spaceType);
      if(result.success) {
        toastUtils.success(`${allSpaces.find(s => s.type === spaceType)?.name} 공간이 생성되었습니다.`);
        await authService.setUserProfile();
      } else {
        toastUtils.error(result.message || '공간 생성 중 오류가 발생했습니다.');
      }        
      
    } catch (error) {
      toastUtils.error('공간 생성 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 공간 접속 핸들러
   */
  const handleAccessSpace = (spaceType: string) => {
    const urls: Record<string, string> = {
      'STARTUP': `${config.startupClientUrl}/${company.code}/company-info`,
      'INVESTOR': `${config.investorClientUrl}/${company.code}/home`,
      'INNOVATE': `${config.innovateClientUrl}/${company.code}/program/list`,
    };
    
    const url = urls[spaceType];
    if (url) {
      window.open(url, '_blank');
    }
  };


  /**
   * 회사 사용자 목록 로드
   */
  const loadCompanyUsers = async () => {
    if (!company) return;
    setIsLoadingUsers(true);
    try {
      const users = await companyUserService.getCompanyUserList(company.id);
      setCompanyUsers(users);
    } catch (error) {
      toastUtils.error('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  /**
   * 마스터 권한 부여
   */
  const handleSetMaster = async (companyUserId: number) => {
    setMasterActionLoading(companyUserId);
    try {
      const result = await companyUserService.setMaster(companyUserId);
      if (result.success) {
        toastUtils.success('마스터 권한이 부여되었습니다.');
        await loadCompanyUsers();
        await authService.setUserProfile();
      } else {
        toastUtils.error(result.message || '권한 부여에 실패했습니다.');
      }
    } catch (error) {
      toastUtils.error('권한 부여 중 오류가 발생했습니다.');
    } finally {
      setMasterActionLoading(null);
    }
  };

  /**
   * 마스터 권한 제거
   */
  const handleRemoveMaster = async (companyUserId: number) => {
    setMasterActionLoading(companyUserId);
    try {
      const result = await companyUserService.removeMaster(companyUserId);
      if (result.success) {
        toastUtils.success('마스터 권한이 제거되었습니다.');
        await loadCompanyUsers();
        await authService.setUserProfile();
      } else {
        toastUtils.error(result.message || '권한 제거에 실패했습니다.');
      }
    } catch (error) {
      toastUtils.error('권한 제거 중 오류가 발생했습니다.');
    } finally {
      setMasterActionLoading(null);
    }
  };

  /**
   * 가입 신청 목록 로드
   */
  const loadApplications = async () => {
    if (!company) return;
    setIsLoadingApplications(true);
    try {
      const apps = await companyUserService.getApplyCompanyUsers(company.id);
      setApplications(apps);
    } catch (error) {
      toastUtils.error('가입 신청 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoadingApplications(false);
    }
  };

  /**
   * 가입 신청 처리 페이지로 이동
   */
  const handleProcessApplication = (notificationId: string) => {
    navigate(`/task/join-apply?n_id=${notificationId}`);
  };

  /**
   * 날짜 포맷팅
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  /**
   * 만료 여부 확인
   */
  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  useEffect(() => {
    
    setHeaderTitle(`${company.name} 관리`);
    clearBackNavigation();
    setBackNavigation('회사 관리', '/space-management');
    
    // 컴포넌트 마운트 시 회사 사용자 목록 로드 (헤더 통계용)
    if (company) {
      loadCompanyUsers();
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'application' && company) {
      loadApplications();
    }
  }, [activeTab, company]);


  return (
    <PageContainer>
      <ContentWrapper>
        {/* 히어로 섹션 */}
        <HeroSection>
          <HeroBackground>
            <HeroGradient />
            <HeroPattern />
          </HeroBackground>
          <HeroContent>
            <HeroAvatarWrapper>
              <Avatar alt={company.name} src={company.logo} isUser={false} sizes="xl" />
              <HeroAvatarBadge>
                <Settings size={14} />
              </HeroAvatarBadge>
            </HeroAvatarWrapper>
            <HeroTextContent>
              <HeroCompanyCode>#{company.code}</HeroCompanyCode>
              <HeroTitle>{company.name}</HeroTitle>
              <HeroDescription>
                회사 스페이스 관리, 마스터 설정, 가입 신청을 처리하세요.
              </HeroDescription>
            </HeroTextContent>
            <HeroStats>
              <HeroStatItem>
                <HeroStatIcon $type="active">
                  <Zap size={18} />
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{activeSpaces.length}</HeroStatValue>
                  <HeroStatLabel>활성 스페이스</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
              <HeroStatDivider />
              <HeroStatItem>
                <HeroStatIcon $type="available">
                  <Crown size={18} />
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{masterUsers.length || '-'}</HeroStatValue>
                  <HeroStatLabel>마스터</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
            </HeroStats>
          </HeroContent>
        </HeroSection>

        {/* 탭 네비게이션 */}
        <TabNavigation>
          <TabButton 
            $active={activeTab === 'space'} 
            onClick={() => setActiveTab('space')}
          >
            <Rocket size={18} />
            <span>스페이스 관리</span>
          </TabButton>
          <TabButton 
            $active={activeTab === 'master'} 
            onClick={() => setActiveTab('master')}
          >
            <Crown size={18} />
            <span>마스터 설정</span>
          </TabButton>
          <TabButton 
            $active={activeTab === 'application'} 
            onClick={() => setActiveTab('application')}
          >
            <UserCheck size={18} />
            <span>가입 신청</span>
          </TabButton>
        </TabNavigation>

        {/* 탭 콘텐츠 */}
        <TabContent>
          {/* 스페이스 관리 탭 */}
          {activeTab === 'space' && (
            <>
              {/* 활성 스페이스 섹션 */}
              <Section>
          <SectionHeader>
            <SectionTitleWrapper>
              <SectionIcon $variant="success">
                <Zap size={20} />
              </SectionIcon>
              <div>
                <SectionTitle>활성 스페이스</SectionTitle>
                <SectionSubtitle>현재 사용 중인 서비스입니다</SectionSubtitle>
              </div>
            </SectionTitleWrapper>
            {activeSpaces.length > 0 && (
              <SectionBadge>{activeSpaces.length}개</SectionBadge>
            )}
          </SectionHeader>
          
          {activeSpaces.length > 0 ? (
            <ActiveSpaceGrid>
              {activeSpaces.map((space, index) => (
                <ActiveSpaceCard 
                  key={space.type}
                  $delay={index * 0.1}
                  $type={space.type}
                  onClick={() => handleAccessSpace(space.type)}
                >
                  <ActiveSpaceTop>
                    <ActiveSpaceIconWrapper $type={space.type}>
                      {space.icon}
                    </ActiveSpaceIconWrapper>
                  <ActiveSpaceInfo>
                    <ActiveSpaceName>{space.name}</ActiveSpaceName>
                    <ActiveSpaceDesc>{space.description}</ActiveSpaceDesc>
                  </ActiveSpaceInfo>
                    <ActiveSpaceArrow>
                      <ExternalLink size={20} />
                    </ActiveSpaceArrow>
                  </ActiveSpaceTop>
                  <ActiveSpaceFeatures>
                    {getSpaceFeatures(space.type).slice(0, 3).map((feature, idx) => (
                      <ActiveFeatureTag key={idx} $type={space.type}>
                        <Check size={12} />
                        {feature}
                      </ActiveFeatureTag>
                    ))}
                  </ActiveSpaceFeatures>
                  <AccessButton $type={space.type}>
                    <span>스페이스 접속</span>
                    <ChevronRight size={16} />
                  </AccessButton>
                </ActiveSpaceCard>
              ))}
            </ActiveSpaceGrid>
          ) : (
            <EmptyActiveSpaces>
              <EmptyIconWrapper>
                <Sparkles size={48} />
              </EmptyIconWrapper>
              <EmptyTitle>활성화된 스페이스가 없습니다</EmptyTitle>
              <EmptyDescription>
                아래에서 원하는 스페이스를 추가하고 시작해보세요!
              </EmptyDescription>
            </EmptyActiveSpaces>
          )}
        </Section>

        {/* 추가 가능한 스페이스 섹션 */}
        {availableSpaces.length > 0 && (
          <Section>
            <SectionHeader>
              <SectionTitleWrapper>
                <SectionIcon $variant="primary">
                  <Plus size={20} />
                </SectionIcon>
                <div>
                  <SectionTitle>추가 가능한 스페이스</SectionTitle>
                  <SectionSubtitle>비즈니스 성장에 필요한 서비스를 추가하세요</SectionSubtitle>
                </div>
              </SectionTitleWrapper>
            </SectionHeader>
            
            <AvailableSpaceGrid>
              {availableSpaces.map((space, index) => (
                <AvailableSpaceCard key={space.type} $delay={index * 0.1}>
                  <AvailableSpaceHeader>
                    <AvailableSpaceIconWrapper $type={space.type}>
                      {space.icon}
                    </AvailableSpaceIconWrapper>
                    <AvailableSpaceTitle>
                      <AvailableSpaceName>{space.name}</AvailableSpaceName>
                      <AvailableSpaceTag $type={space.type}>추가 가능</AvailableSpaceTag>
                    </AvailableSpaceTitle>
                  </AvailableSpaceHeader>

                  <AvailableSpaceDesc>
                    {getServiceDetailDescription(space.type)}
                  </AvailableSpaceDesc>

                  <AvailableSpaceFeatures>
                    <FeaturesTitle>주요 기능</FeaturesTitle>
                    <FeaturesList>
                      {getSpaceFeatures(space.type).map((feature, idx) => (
                        <FeatureItem key={idx}>
                          <FeatureIcon $type={space.type}>
                            <Check size={14} />
                          </FeatureIcon>
                        <FeatureText>{feature}</FeatureText>
                      </FeatureItem>
                    ))}
                    </FeaturesList>
                  </AvailableSpaceFeatures>
                  
                  <AddSpaceButton
                    $type={space.type}
                      onClick={() => handleCreateSpace(space.type)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <LoadingSpinner />
                        처리 중...
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        스페이스 추가하기
                      </>
                    )}
                  </AddSpaceButton>
                </AvailableSpaceCard>
              ))}
            </AvailableSpaceGrid>
          </Section>
        )}

              {/* 모든 서비스 이용 중 */}
        {availableSpaces.length === 0 && activeSpaces.length > 0 && (
                <AllServicesCard>
                  <AllServicesIcon>
                    <Award size={48} />
                  </AllServicesIcon>
                  <AllServicesTitle>모든 스페이스를 이용 중입니다! 🎉</AllServicesTitle>
                  <AllServicesDesc>
                    {company.name}에서 제공하는 모든 스페이스를 사용하고 계십니다.
                    <br />
              새로운 서비스가 출시되면 알려드리겠습니다.
                  </AllServicesDesc>
                </AllServicesCard>
              )}
            </>
          )}

          {/* 마스터 설정 탭 */}
          {activeTab === 'master' && (
            <>
              <Section>
                <SectionHeader>
                  <SectionTitleWrapper>
                    <SectionIcon $variant="warning">
                      <Crown size={20} />
                    </SectionIcon>
                    <div>
                      <SectionTitle>현재 마스터</SectionTitle>
                      <SectionSubtitle>회사 마스터 권한을 가진 팀원 목록입니다</SectionSubtitle>
                    </div>
                  </SectionTitleWrapper>
                  <AddMasterButton onClick={() => setShowAddMasterPanel(!showAddMasterPanel)}>
                    <Plus size={18} />
                    <span>마스터 추가</span>
                  </AddMasterButton>
                </SectionHeader>

                {isLoadingUsers ? (
                  <LoadingState>
                    <LoadingSpinner />
                    <span>마스터 목록을 불러오는 중...</span>
                  </LoadingState>
                ) : masterUsers.length > 0 ? (
                  <UserGrid>
                    {masterUsers.map((member, index) => (
                      <UserCard key={member.id} $delay={index * 0.05}>
                        <UserCardHeader>
                          <UserAvatar>
                            <Avatar alt={member.name || '사용자'} isUser={true} sizes="md" />
                          </UserAvatar>
                          <UserInfo>
                            <UserNameRow>
                              <UserName>{member.name || '이름 없음'}</UserName>
                              <MasterBadge>
                                <Crown size={12} />
                                마스터
                              </MasterBadge>
                            </UserNameRow>
                            <UserMeta>
                              <UserEmail>{member.email || '이메일 없음'}</UserEmail>
                              {member.position && (
                                <UserPosition>{member.position}</UserPosition>
                              )}
                            </UserMeta>
                          </UserInfo>
                        </UserCardHeader>
                        <UserCardFooter>
                          {masterUsers.length === 1 ? (
                            <LastMasterNotice>
                              <ShieldCheck size={14} />
                              <span>유일한 마스터 (해제 불가)</span>
                            </LastMasterNotice>
                          ) : (
                            <MasterActionButton
                              $variant="remove"
                              onClick={() => handleRemoveMaster(member.id)}
                              disabled={masterActionLoading === member.id}
                            >
                              {masterActionLoading === member.id ? (
                                <LoadingSpinner />
                              ) : (
                                <>
                                  <ShieldX size={16} />
                                  <span>마스터 해제</span>
                                </>
                              )}
                            </MasterActionButton>
                          )}
                        </UserCardFooter>
                      </UserCard>
                    ))}
                  </UserGrid>
                ) : (
                  <EmptyActiveSpaces>
                    <EmptyIconWrapper>
                      <Crown size={48} />
                    </EmptyIconWrapper>
                    <EmptyTitle>마스터 정보를 불러오는 중...</EmptyTitle>
                    <EmptyDescription>
                      회사에는 최소 1명의 마스터가 필요합니다.
            </EmptyDescription>
                  </EmptyActiveSpaces>
                )}
              </Section>

            </>
          )}

          {/* 마스터 추가 모달 */}
          {showAddMasterPanel && (
            <ModalOverlay onClick={() => { setShowAddMasterPanel(false); setSearchQuery(''); }}>
              <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <ModalIconWrapper>
                    <Crown size={24} />
                  </ModalIconWrapper>
                  <ModalTitleArea>
                    <ModalTitle>마스터 추가</ModalTitle>
                    <ModalSubtitle>팀원 중에서 마스터로 지정할 사람을 선택하세요</ModalSubtitle>
                  </ModalTitleArea>
                  <ModalCloseButton onClick={() => { setShowAddMasterPanel(false); setSearchQuery(''); }}>
                    ✕
                  </ModalCloseButton>
                </ModalHeader>

                {/* 검색 입력 */}
                {nonMasterUsers.length > 0 && (
                  <SearchInputWrapper>
                    <SearchIcon>
                      <Search size={18} />
                    </SearchIcon>
                    <SearchInput
                      type="text"
                      placeholder="이름, 이메일, 직책으로 검색..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                    />
                    {searchQuery && (
                      <SearchClearButton onClick={() => setSearchQuery('')}>
                        <X size={16} />
                      </SearchClearButton>
                    )}
                  </SearchInputWrapper>
                )}
                
                <ModalContent>
                  {nonMasterUsers.length > 0 ? (
                    filteredNonMasterUsers.length > 0 ? (
                      <MemberSelectList>
                        {filteredNonMasterUsers.map((member) => (
                          <MemberSelectItem key={member.id}>
                            <MemberSelectInfo>
                              <Avatar alt={member.name || '사용자'} isUser={true} sizes="md" />
                              <MemberSelectDetails>
                                <MemberSelectName>{member.name || '이름 없음'}</MemberSelectName>
                                <MemberSelectEmail>{member.email || '이메일 없음'}</MemberSelectEmail>
                                {member.position && (
                                  <MemberSelectPosition>{member.position}</MemberSelectPosition>
                                )}
                              </MemberSelectDetails>
                            </MemberSelectInfo>
                            <MasterActionButton
                              $variant="add"
                              onClick={() => handleSetMaster(member.id)}
                              disabled={masterActionLoading === member.id}
                            >
                              {masterActionLoading === member.id ? (
                                <LoadingSpinner />
                              ) : (
                                <>
                                  <ShieldCheck size={16} />
                                  <span>마스터 지정</span>
                                </>
                              )}
                            </MasterActionButton>
                          </MemberSelectItem>
                        ))}
                      </MemberSelectList>
                    ) : (
                      <NoSearchResultMessage>
                        <Search size={32} />
                        <span>"{searchQuery}" 검색 결과가 없습니다</span>
                      </NoSearchResultMessage>
                    )
                  ) : (
                    <NoMembersMessage>
                      <NoMembersIcon>
                        <Users size={48} />
                      </NoMembersIcon>
                      <NoMembersTitle>추가할 팀원이 없습니다</NoMembersTitle>
                      <NoMembersDesc>
                        모든 팀원이 이미 마스터이거나,<br />
                        아직 팀원이 없습니다.
                      </NoMembersDesc>
                    </NoMembersMessage>
                  )}
                </ModalContent>

                <ModalFooter>
                  <ModalFooterText>
                    <Users size={14} />
                    <span>
                      {searchQuery 
                        ? `${filteredNonMasterUsers.length}명 검색됨 (전체 ${nonMasterUsers.length}명)`
                        : `총 ${nonMasterUsers.length}명의 팀원`
                      }
                    </span>
                  </ModalFooterText>
                  <ModalCloseAction onClick={() => { setShowAddMasterPanel(false); setSearchQuery(''); }}>
                    닫기
                  </ModalCloseAction>
                </ModalFooter>
              </ModalContainer>
            </ModalOverlay>
          )}

          {/* 가입 신청 탭 */}
          {activeTab === 'application' && (
            <>
              {/* 대기 중인 신청 */}
              <Section>
                <SectionHeader>
                  <SectionTitleWrapper>
                    <SectionIcon $variant="warning">
                      <Clock size={20} />
                    </SectionIcon>
                    <div>
                      <SectionTitle>대기 중인 신청</SectionTitle>
                      <SectionSubtitle>승인 또는 거절이 필요한 가입 신청입니다</SectionSubtitle>
                    </div>
                  </SectionTitleWrapper>
                  {pendingApplications.length > 0 && (
                    <SectionBadge>{pendingApplications.length}건</SectionBadge>
                  )}
                </SectionHeader>

                {isLoadingApplications ? (
                  <LoadingState>
                    <LoadingSpinner />
                    <span>가입 신청 목록을 불러오는 중...</span>
                  </LoadingState>
                ) : pendingApplications.length > 0 ? (
                  <ApplicationGrid>
                    {pendingApplications.map((app, index) => (
                      <ApplicationCard 
                        key={app.notificationId} 
                        $delay={index * 0.05}
                        $status="pending"
                      >
                        <ApplicationCardHeader>
                          <ApplicationAvatar>
                            <Avatar alt={app.name || '사용자'} isUser={true} sizes="md" />
                          </ApplicationAvatar>
                          <ApplicationInfo>
                            <ApplicationName>{app.name || '이름 없음'}</ApplicationName>
                            <ApplicationEmail>{app.email || '이메일 없음'}</ApplicationEmail>
                          </ApplicationInfo>
                          <ApplicationStatusBadge $status="pending">
                            <Clock size={12} />
                            대기 중
                          </ApplicationStatusBadge>
                        </ApplicationCardHeader>
                        <ApplicationCardBody>
                          <ApplicationMeta>
                            <ApplicationMetaItem>
                              <AlertCircle size={14} />
                              <span>
                                {isExpired(app.expiresAt) 
                                  ? '만료됨' 
                                  : `만료일: ${formatDate(app.expiresAt)}`
                                }
                              </span>
                            </ApplicationMetaItem>
                          </ApplicationMeta>
                        </ApplicationCardBody>
                        <ApplicationCardFooter>
                          <ProcessButton onClick={() => handleProcessApplication(app.notificationId)}>
                            <UserCheck size={16} />
                            <span>신청 처리하기</span>
                            <ChevronRight size={16} />
                          </ProcessButton>
                        </ApplicationCardFooter>
                      </ApplicationCard>
                    ))}
                  </ApplicationGrid>
                ) : (
                  <EmptyActiveSpaces>
                    <EmptyIconWrapper>
                      <Inbox size={48} />
                    </EmptyIconWrapper>
                    <EmptyTitle>대기 중인 신청이 없습니다</EmptyTitle>
                    <EmptyDescription>
                      새로운 가입 신청이 있으면 여기에 표시됩니다.
                    </EmptyDescription>
                  </EmptyActiveSpaces>
                )}
              </Section>

              {/* 처리된 신청 */}
              {processedApplications.length > 0 && (
                <Section>
                  <SectionHeader>
                    <SectionTitleWrapper>
                      <SectionIcon $variant="success">
                        <CheckCircle2 size={20} />
                      </SectionIcon>
                      <div>
                        <SectionTitle>처리된 신청</SectionTitle>
                        <SectionSubtitle>이미 승인 또는 거절된 신청 내역입니다</SectionSubtitle>
                      </div>
                    </SectionTitleWrapper>
                  </SectionHeader>

                  <ApplicationGrid>
                    {processedApplications.map((app, index) => (
                      <ApplicationCard 
                        key={app.notificationId} 
                        $delay={index * 0.05}
                        $status={app.isAccepted ? 'accepted' : 'rejected'}
                      >
                        <ApplicationCardHeader>
                          <ApplicationAvatar>
                            <Avatar alt={app.name || '사용자'} isUser={true} sizes="md" />
                          </ApplicationAvatar>
                          <ApplicationInfo>
                            <ApplicationName>{app.name || '이름 없음'}</ApplicationName>
                            <ApplicationEmail>{app.email || '이메일 없음'}</ApplicationEmail>
                          </ApplicationInfo>
                          <ApplicationStatusBadge $status={app.isAccepted ? 'accepted' : 'rejected'}>
                            {app.isAccepted ? (
                              <>
                                <CheckCircle2 size={12} />
                                승인됨
                              </>
                            ) : (
                              <>
                                <XCircle size={12} />
                                거절됨
                              </>
                            )}
                          </ApplicationStatusBadge>
                        </ApplicationCardHeader>
                        {app.rejectReason && (
                          <ApplicationCardBody>
                            <RejectReasonBox>
                              <RejectReasonLabel>거절 사유</RejectReasonLabel>
                              <RejectReasonText>{app.rejectReason}</RejectReasonText>
                            </RejectReasonBox>
                          </ApplicationCardBody>
                        )}
                      </ApplicationCard>
                    ))}
                  </ApplicationGrid>
                </Section>
              )}
            </>
          )}
        </TabContent>
      </ContentWrapper>
    </PageContainer>
  );
};

/**
 * 공간 타입별 주요 기능 반환
 */
const getSpaceFeatures = (spaceType: string): string[] => {
  switch (spaceType) {
    case 'STARTUP':
      return [
        '회사 정보 관리',
        '투자 유치 현황',
        '사업 계획서 관리',
        '투자자 연결',
      ];
    case 'INVESTOR':
      return [
        '포트폴리오 관리',
        '딜 소싱',
        '투자 분석',
        '리포트 생성',
      ];
    case 'INNOVATE':
      return [
        '프로그램 운영',
        '참가자 관리',
        '평가 시스템',
        '성과 분석',
      ];
    default:
      return [];
  }
};

/**
 * 서비스별 상세 설명 반환
 */
const getServiceDetailDescription = (spaceType: string): string => {
  switch (spaceType) {
    case 'STARTUP':
      return '스타트업의 성장 단계별 맞춤형 솔루션을 제공합니다. 투자 유치부터 사업 확장까지 필요한 모든 도구와 네트워크를 한 곳에서 만나보세요.';
    case 'INVESTOR':
      return '전문적인 투자 관리 플랫폼으로 딜 소싱, 포트폴리오 추적, 실사 관리까지 투자 업무의 모든 과정을 효율적으로 관리할 수 있습니다.';
    case 'INNOVATE':
      return '혁신 프로그램을 체계적으로 운영할 수 있는 통합 플랫폼입니다. 참가자 선발부터 성과 측정까지 프로그램의 전 과정을 디지털화하세요.';
    default:
      return '';
  }
};

// ============================================
// Animations
// ============================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// Styled Components
// ============================================

const PageContainer = styled.div`
  min-height: 100%;
  height: 100%;
  padding: 1.5rem;
  background: ${semanticColors.background.primary};
  overflow-y: auto;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
`;

// 히어로 섹션
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 2rem;
  border-radius: 24px;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%);
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 40%);
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 2.5rem;
  
  @media (max-width: 768px) {
  flex-direction: column;
    text-align: center;
    padding: 2rem;
  }
`;

const HeroAvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const HeroAvatarBadge = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: 3px solid #059669;
  border-radius: 50%;
  color: #fff;
`;

const HeroTextContent = styled.div`
  flex: 1;
`;

const HeroCompanyCode = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: rgba(255,255,255,0.2);
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  color: rgba(255,255,255,0.9);
  margin-bottom: 0.5rem;
  font-family: 'Monaco', 'Consolas', monospace;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const HeroDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.8);
  max-width: 400px;
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 16px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const HeroStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeroStatIcon = styled.div<{ $type: 'active' | 'available' }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
`;

const HeroStatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeroStatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
`;

const HeroStatLabel = styled.span`
  ${typography.caption.caption2}
  color: rgba(255,255,255,0.7);
`;

const HeroStatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: rgba(255,255,255,0.2);
`;

// 탭 네비게이션
const TabNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.05s both;
  
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' 
    : 'transparent'};
  border: none;
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: ${props => props.$active ? '#fff' : semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' 
      : semanticColors.background.tertiary};
    color: ${props => props.$active ? '#fff' : semanticColors.text.primary};
  }
  
  svg {
    flex-shrink: 0;
  }
`;

const TabContent = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
`;

// 섹션 스타일
const Section = styled.section`
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SectionIcon = styled.div<{ $variant: 'primary' | 'success' | 'warning' }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  
  ${props => props.$variant === 'primary' && `
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.brand};
  `}
  
  ${props => props.$variant === 'success' && `
    background: ${semanticColors.background.success.secondary};
    color: ${semanticColors.text.onSuccess.primary};
  `}
  
  ${props => props.$variant === 'warning' && `
    background: ${semanticColors.background.warning.secondary};
    color: ${semanticColors.text.onWarning.primary};
  `}
`;

const SectionTitle = styled.h2`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const SectionSubtitle = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-top: 0.125rem;
`;

const SectionBadge = styled.span`
  padding: 0.375rem 0.875rem;
  background: ${semanticColors.background.success.secondary};
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.onSuccess.primary};
`;

// 활성 스페이스 그리드
const ActiveSpaceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
`;

const ActiveSpaceCard = styled.div<{ $delay: number; $type: string }>`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 2px solid ${semanticColors.border.primary};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.25s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 20px 20px 0 0;
    background: ${props => {
      switch(props.$type) {
        case 'STARTUP': return 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)';
        case 'INVESTOR': return 'linear-gradient(135deg, #10b981 0%, #34d399 100%)';
        case 'INNOVATE': return 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)';
        default: return semanticColors.background.brand;
      }
    }};
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  
  position: relative;
  
  &:hover {
    border-color: ${props => {
      switch(props.$type) {
        case 'STARTUP': return '#3b82f6';
        case 'INVESTOR': return '#10b981';
        case 'INNOVATE': return '#f59e0b';
        default: return semanticColors.border.brand;
      }
    }};
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.1);
    
    &::before {
      opacity: 1;
    }
  }
`;

const ActiveSpaceTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ActiveSpaceIconWrapper = styled.div<{ $type: string }>`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  flex-shrink: 0;
  transition: all 0.25s ease;
  
  ${props => props.$type === 'STARTUP' && `
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(96, 165, 250, 0.2) 100%);
    color: #3b82f6;
  `}
  
  ${props => props.$type === 'INVESTOR' && `
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(52, 211, 153, 0.2) 100%);
    color: #10b981;
  `}
  
  ${props => props.$type === 'INNOVATE' && `
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(251, 191, 36, 0.2) 100%);
    color: #f59e0b;
  `}
  
  ${ActiveSpaceCard}:hover & {
    transform: scale(1.05);
  }
`;

const ActiveSpaceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActiveSpaceName = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const ActiveSpaceDesc = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const ActiveSpaceArrow = styled.div`
  color: ${semanticColors.text.tertiary};
  transition: all 0.25s ease;
  
  ${ActiveSpaceCard}:hover & {
    color: ${semanticColors.text.brand};
    transform: translateX(4px);
  }
`;

const ActiveSpaceFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const ActiveFeatureTag = styled.span<{ $type: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 100px;
  ${typography.caption.caption2}
  
  ${props => props.$type === 'STARTUP' && `
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  `}
  
  ${props => props.$type === 'INVESTOR' && `
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  `}
  
  ${props => props.$type === 'INNOVATE' && `
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  `}
`;

const AccessButton = styled.div<{ $type: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  transition: all 0.2s ease;
  
  ${props => props.$type === 'STARTUP' && `
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  `}
  
  ${props => props.$type === 'INVESTOR' && `
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  `}
  
  ${props => props.$type === 'INNOVATE' && `
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  `}
  
  ${ActiveSpaceCard}:hover & {
    ${props => props.$type === 'STARTUP' && `
      background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
      color: #fff;
    `}
    
    ${props => props.$type === 'INVESTOR' && `
      background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
      color: #fff;
    `}
    
    ${props => props.$type === 'INNOVATE' && `
      background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
      color: #fff;
    `}
  }
`;

// 추가 가능 스페이스 그리드
const AvailableSpaceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
`;

const AvailableSpaceCard = styled.div<{ $delay: number }>`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  transition: all 0.25s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  
  &:hover {
    border-color: ${semanticColors.border.secondary};
    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
  }
`;

const AvailableSpaceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const AvailableSpaceIconWrapper = styled.div<{ $type: string }>`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  flex-shrink: 0;
  
  ${props => props.$type === 'STARTUP' && `
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(96, 165, 250, 0.2) 100%);
    color: #3b82f6;
  `}
  
  ${props => props.$type === 'INVESTOR' && `
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(52, 211, 153, 0.2) 100%);
    color: #10b981;
  `}
  
  ${props => props.$type === 'INNOVATE' && `
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(251, 191, 36, 0.2) 100%);
    color: #f59e0b;
  `}
`;

const AvailableSpaceTitle = styled.div`
  flex: 1;
`;

const AvailableSpaceName = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const AvailableSpaceTag = styled.span<{ $type: string }>`
  display: inline-flex;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  ${typography.caption.caption2}
  
  ${props => props.$type === 'STARTUP' && `
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  `}
  
  ${props => props.$type === 'INVESTOR' && `
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  `}
  
  ${props => props.$type === 'INNOVATE' && `
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  `}
`;

const AvailableSpaceDesc = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
  margin-bottom: 1.25rem;
`;

const AvailableSpaceFeatures = styled.div`
  flex: 1;
  margin-bottom: 1.25rem;
`;

const FeaturesTitle = styled.h4`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.tertiary};
  margin-bottom: 0.75rem;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FeatureIcon = styled.div<{ $type: string }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  ${props => props.$type === 'STARTUP' && `
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  `}
  
  ${props => props.$type === 'INVESTOR' && `
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  `}
  
  ${props => props.$type === 'INNOVATE' && `
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  `}
`;

const FeatureText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
`;

const AddSpaceButton = styled.button<{ $type: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem;
  border: none;
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.25s ease;
  
  ${props => props.$type === 'STARTUP' && `
    background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
    
    &:hover:not(:disabled) {
      box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.$type === 'INVESTOR' && `
    background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
    
    &:hover:not(:disabled) {
      box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.$type === 'INNOVATE' && `
    background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
    
    &:hover:not(:disabled) {
      box-shadow: 0 8px 20px rgba(245, 158, 11, 0.3);
      transform: translateY(-2px);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// 빈 상태
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  padding: 2rem;
`;

const EmptyActiveSpaces = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  background: ${semanticColors.background.secondary};
  border: 2px dashed ${semanticColors.border.primary};
  border-radius: 20px;
  text-align: center;
`;

const EmptyIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.primary};
  border: 2px dashed ${semanticColors.border.primary};
  border-radius: 20px;
  color: ${semanticColors.text.disabled};
  margin-bottom: 1.5rem;
`;

const EmptyTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`;

const EmptyDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  max-width: 300px;
`;

// 로딩 상태
const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
`;

// 사용자 그리드
const UserGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const UserCard = styled.div<{ $delay: number }>`
  display: flex;
    flex-direction: column;
  padding: 1.25rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  
  &:hover {
    border-color: ${semanticColors.border.secondary};
    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
  }
`;

const UserCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const UserAvatar = styled.div`
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const UserName = styled.span`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
`;

const MasterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 6px;
  ${typography.caption.caption2}
  color: ${semanticColors.text.onWarning.primary};
  font-weight: 600;
`;

const UserMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const UserEmail = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const UserPosition = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.secondary};
  padding: 0.125rem 0.5rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 4px;
`;

const UserCardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const LastMasterNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 8px;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.onWarning.primary};
`;

const MasterActionButton = styled.button<{ $variant: 'add' | 'remove' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 8px;
  ${typography.caption.caption1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$variant === 'add' && `
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.brand};
  
  &:hover:not(:disabled) {
    background: ${semanticColors.background.brand};
      color: #fff;
    }
  `}
  
  ${props => props.$variant === 'remove' && `
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.secondary};
    
    &:hover:not(:disabled) {
      background: ${semanticColors.background.danger.primary};
      color: #fff;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// 마스터 추가 버튼
const AddMasterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: none;
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.3);
  }
`;

// 모달 스타일
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 85vh;
  background: ${semanticColors.background.primary};
  border-radius: 24px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  animation: ${fadeInUp} 0.3s ease-out;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1.25rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, transparent 100%);
`;

const ModalIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 14px;
  color: #fff;
  flex-shrink: 0;
`;

const ModalTitleArea = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const ModalSubtitle = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const ModalCloseButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border: none;
  border-radius: 10px;
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: 1.25rem;
  
  &:hover {
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.primary};
  }
`;

// 검색 입력
const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  margin-bottom: 0.5rem;
  margin-top: 1rem;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 2.25rem;
  color: ${semanticColors.text.tertiary};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 2.75rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  transition: all 0.2s ease;
  
  &::placeholder {
    color: ${semanticColors.text.disabled};
  }
  
  &:focus {
    outline: none;
    border-color: ${semanticColors.border.brand};
    background: ${semanticColors.background.primary};
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }
`;

const SearchClearButton = styled.button`
  position: absolute;
  right: 2rem;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border: none;
  border-radius: 6px;
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.primary};
  }
`;

const NoSearchResultMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 2rem;
  color: ${semanticColors.text.tertiary};
  ${typography.paragraph.p2.default}
  text-align: center;
  
  svg {
    opacity: 0.4;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-top: 1px solid ${semanticColors.border.primary};
  background: ${semanticColors.background.secondary};
`;

const ModalFooterText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const ModalCloseAction = styled.button`
  padding: 0.625rem 1.25rem;
  background: ${semanticColors.background.tertiary};
  border: none;
  border-radius: 8px;
  ${typography.paragraph.p2.default}
  font-weight: 500;
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.primary};
    color: ${semanticColors.text.primary};
  }
`;

const MemberSelectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MemberSelectItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${semanticColors.border.brand};
    background: ${semanticColors.background.tertiary};
    transform: translateX(4px);
  }
`;

const MemberSelectInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex: 1;
  min-width: 0;
`;

const MemberSelectDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberSelectName = styled.p`
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: ${semanticColors.text.primary};
  margin-bottom: 0.125rem;
`;

const MemberSelectEmail = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MemberSelectPosition = styled.span`
  display: inline-block;
  margin-top: 0.375rem;
  padding: 0.125rem 0.5rem;
  background: ${semanticColors.background.brand};
  border-radius: 4px;
  ${typography.caption.caption2}
  color: #fff;
`;

const NoMembersMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  text-align: center;
`;

const NoMembersIcon = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 50%;
  color: ${semanticColors.text.disabled};
  margin-bottom: 1.25rem;
`;

const NoMembersTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`;

const NoMembersDesc = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  line-height: 1.6;
`;

// 모든 서비스 이용 중
const AllServicesCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, ${semanticColors.background.success.secondary} 0%, rgba(16, 185, 129, 0.05) 100%);
  border: 1px solid ${semanticColors.border.success};
  border-radius: 24px;
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

const AllServicesIcon = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  border-radius: 50%;
  color: #fff;
  margin-bottom: 1.5rem;
`;

const AllServicesTitle = styled.h3`
  ${typography.heading.h4}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.75rem;
`;

const AllServicesDesc = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
`;

// 가입 신청 관련 스타일
const ApplicationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
`;

const ApplicationCard = styled.div<{ $delay: number; $status: 'pending' | 'accepted' | 'rejected' }>`
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'pending': return semanticColors.border.warning;
      case 'accepted': return semanticColors.border.success;
      case 'rejected': return semanticColors.border.dangerPrimary;
      default: return semanticColors.border.primary;
    }
  }};
  border-radius: 16px;
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  
  ${props => props.$status === 'pending' && `
    border-left: 4px solid #f59e0b;
  `}
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }
`;

const ApplicationCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1rem;
`;

const ApplicationAvatar = styled.div`
  flex-shrink: 0;
`;

const ApplicationInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ApplicationName = styled.p`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.125rem;
`;

const ApplicationEmail = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ApplicationStatusBadge = styled.span<{ $status: 'pending' | 'accepted' | 'rejected' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 100px;
  ${typography.caption.caption2}
  font-weight: 600;
  flex-shrink: 0;
  
  ${props => props.$status === 'pending' && `
    background: ${semanticColors.background.warning.secondary};
    color: ${semanticColors.text.onWarning.primary};
  `}
  
  ${props => props.$status === 'accepted' && `
    background: ${semanticColors.background.success.secondary};
    color: ${semanticColors.text.onSuccess.primary};
  `}
  
  ${props => props.$status === 'rejected' && `
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.primary};
  `}
`;

const ApplicationCardBody = styled.div`
  padding-top: 0.75rem;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const ApplicationMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ApplicationMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  ${typography.caption.caption2}
  color: ${semanticColors.text.secondary};
  
  svg {
    color: ${semanticColors.text.tertiary};
  }
`;

const ApplicationCardFooter = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const ProcessButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: none;
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.3);
  }
  
  svg:last-child {
    margin-left: auto;
  }
`;

const RejectReasonBox = styled.div`
  padding: 0.75rem;
  background: ${semanticColors.background.danger.secondary};
  border-radius: 8px;
  margin-top: 0.5rem;
`;

const RejectReasonLabel = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.onDanger.primary};
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const RejectReasonText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.onDanger.secondary};
`;

export default CompanySpaceManagement;
